import { computed } from "mobx";
import { _async, _await, Model, model, modelFlow, prop } from "mobx-keystone";
import { MenuItem } from "pages/Workspace";
import api from "../api";
import { AuthenticatedUser } from "../api/types";
import { flushAuthUser, saveAuthUser, Toast } from "../utils";

@model("collab/AuthStore")
export default class AuthStore extends Model({
  user: prop<AuthenticatedUser | null>(null),
}) {
  @modelFlow
  checkAuth = _async(function* (this: AuthStore) {
    const authenticatedUser = yield* _await(api.auth.checkAuth());
    this.user = authenticatedUser as AuthenticatedUser;

    api.config.token = this.user?.accessToken || "";

    return {
      user: authenticatedUser,
      isAuthenticated: !!authenticatedUser,
    };
  });

  @modelFlow
  login = _async(function* (this: AuthStore, code: string | undefined) {
    if (code) {
      try {
        const response = yield* _await(api.auth.login(code));
        const user = response as AuthenticatedUser;
        this.user = user;
        _await(saveAuthUser(user));

        return response;
      } catch (error) {
        throw error;
      }
    }
  });

  @modelFlow
  logout = _async(function* (this: AuthStore) {
    yield* _await(api.auth.logout());
    yield* _await(flushAuthUser());
    this.user = null;
  });

  @computed
  get current() {
    return this.user ? this.user.user : undefined;
  }

  @computed
  get isRbacAdmin() {
    return this.current?.rbacRole?.key === 'admin';
  }

  // RBAC admins act as project admin on every project
  isProjectAdmin(project?: { adminUsers: number[] } | null): boolean {
    const current = this.current;
    if (!current || !project) return false;
    return this.isRbacAdmin || project.adminUsers.includes(current.id);
  }

  getPermissions(section: string, subsection: string) {
    try{
      if(this.user){
        const permissionsArray = this.user.user?.rbacRole?.permissions;
        return permissionsArray?.find((item) => {
          return item.section === section && item.subsection === subsection;
        })?.accessLevel;
      }
    }catch(e){
      Toast.danger("Unable to resolve roles");
    }
  }

  mapMenuItemsPerRoles(menuItems: MenuItem[]) : MenuItem[] {
    try {
      if(this.user){
        const permissionsArray = this.user.user?.rbacRole?.permissions;
        const availableSections = permissionsArray?.map((item) => {
          return item.section;
        });
        availableSections?.push('settings', 'status');
        menuItems.forEach((item) => {
          if(availableSections && !availableSections.includes(item.text.toLowerCase())){
            item.disable = true;
          }
        });
        return menuItems;
      };
      return menuItems;

    }catch(e){
      Toast.danger("Unable to resolve roles");
      return menuItems;
    }
  }

  havePermissions(subSection:string, section: string) : boolean {
    return !!(this.getPermissions(section, subSection)?.toLowerCase() === 'edit');
  }
}
